// Call-recordings portal — constants & small pure helpers.

import { CallDirection, RecordingsRole } from './types';

// ---------- Firestore collections ----------
export const DB_RECORDINGS = 'recordings';
export const DB_ACCESS_AUDIT = 'access_audit';

// ---------- Callable Cloud Function names ----------
// Playback mints a presigned URL server-side (after authz + audit). Never
// generate presigned URLs client-side — the AWS creds live only in the
// function.
export const FN_GENERATE_RECORDING_URL = 'generateRecordingUrl';

// ---------- Retention ----------
// 30-day window, measured from the CALL time (recorded_at), per the confirmed
// spec. The S3 lifecycle rule is the authoritative deletion; expires_at lets
// the list hide rows whose audio has already been lifecycled away (avoids a
// 404 on play).
export const RETENTION_DAYS = 30;
export const RETENTION_MS = RETENTION_DAYS * 24 * 60 * 60 * 1000;

// ---------- Presigned URL ----------
// TTL ceiling for playback URLs (spec: <= 15 min). Kept here so the client can
// display "link valid for N minutes" without hardcoding.
export const PRESIGNED_URL_TTL_SECONDS = 15 * 60;

// ---------- Roles ----------
export const RECORDINGS_ROLE_LABELS: Record<RecordingsRole, string> = {
  agent: 'Agent',
  supervisor: 'Supervisor',
  qa_lead: 'QA Lead',
  admin: 'Admin',
};

// Strictest (most access) first — used by role pickers.
export const RECORDINGS_ROLE_ORDER: RecordingsRole[] = [
  'admin',
  'supervisor',
  'qa_lead',
  'agent',
];

export const CALL_DIRECTION_LABELS: Record<CallDirection, string> = {
  inbound: 'Inbound',
  outbound: 'Outbound',
};

// Default + max page sizes for the list API.
export const RECORDINGS_PAGE_SIZE = 25;
export const RECORDINGS_PAGE_SIZE_MAX = 100;

// The UI loads a bounded working set (scope + date range) and then filters it
// live/client-side for partial-match search. This caps how many rows we pull
// into the browser per chunk; "Load older" pages past it with a cursor.
export const RECORDINGS_WORKING_SET_CAP = 1000;

// Global identifier search (call ID / caller # / agent): prefix queries pushed
// to the server so an ID lookup covers the FULL scoped history, never just the
// loaded working set. Per-query row cap + minimum typed length before firing.
export const RECORDINGS_GLOBAL_SEARCH_LIMIT = 100;
export const RECORDINGS_GLOBAL_SEARCH_MIN_CHARS = 2;

// ---------- Display helpers ----------

// Mask a phone number for display (PHI minimization): keep the last 4 digits.
// "+13105551234" -> "•••••••1234". Returns '—' for empty.
export function maskPhone(num: string | undefined | null): string {
  if (!num) return '—';
  const digits = num.replace(/\D/g, '');
  if (digits.length <= 4) return num;
  const last4 = digits.slice(-4);
  return '•••••••' + last4;
}

// Human-readable direction label with a safe fallback.
export function directionLabel(d: string | undefined | null): string {
  if (d === 'inbound' || d === 'outbound') return CALL_DIRECTION_LABELS[d];
  return '—';
}
